import {
  Box,
  ClickAwayListener,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  useTheme
} from '@mui/material'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import * as Styled from '../styled'
import { PopupInstance } from 'components/popup/Popup'
import { EventDoc, EventReminder, TaskType } from 'types/api/changes'
import PopupSection from 'components/popup/PopupSection'
import Button from 'components/buttons/Button'
import { t } from 'i18next'
import Logger from 'services/Logger'
import dayjs, { Dayjs } from 'dayjs'
import { ReminderDivider } from './AddReminderPopup'
import { DateRangePicker } from 'components/calendar/ViewDatesPicker'
import { DateRange } from 'react-day-picker'
import AbsoluteReminderTimePicker from './AbsoluteReminderTimePicker'
import { getDefaultTimezone } from 'index'

interface ContentProps {
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  instance?: PopupInstance | null | undefined
  editingReminder?: EventReminder | undefined
  setOpen?: (b: boolean) => void
  event?: EventDoc
}

export enum ReminderType {
  MIN = 'minutes',
  HOUR = 'hours',
  DAY = 'days',
  WEEK = 'weeks'
}

enum ReminderMode {
  RELATIVE = 'relative', // X minutes/hours/days/weeks before
  ABSOLUTE = 'absolute' // Specific date/time
}

interface CustomReminder {
  type: ReminderType
  title: string
}

function unitI18nKey(type: ReminderType): 'minute' | 'hour' | 'day' | 'week' {
  switch (type) {
    case ReminderType.MIN:
      return 'minute'
    case ReminderType.HOUR:
      return 'hour'
    case ReminderType.DAY:
      return 'day'
    case ReminderType.WEEK:
      return 'week'
  }
}

export function CustomReminderContent(props: ContentProps) {
  const theme = useTheme()
  const isAllDay = props.event?.AllDay === '1'

  // Determine available reminder types based on whether event is all-day
  const availableReminderTypes: CustomReminder[] = isAllDay
    ? [
        { type: ReminderType.DAY, title: t('day', { count: 1 }) },
        { type: ReminderType.WEEK, title: t('week', { count: 1 }) }
      ]
    : [
        { type: ReminderType.MIN, title: t('minute', { count: 1 }) },
        { type: ReminderType.HOUR, title: t('hour', { count: 1 }) },
        { type: ReminderType.DAY, title: t('day', { count: 1 }) },
        { type: ReminderType.WEEK, title: t('week', { count: 1 }) }
      ]

  const [mode, setMode] = useState<ReminderMode | undefined>(undefined)
  const [searchText, setSearchText] = useState<string>(
    props.editingReminder?.offset !== undefined
      ? offsetToText(props.editingReminder)
      : props.event?.AllDay === '1'
        ? '1'
        : '15'
  )
  const [currentType, setCurrentType] = useState<CustomReminder>(
    props.editingReminder !== undefined
      ? (availableReminderTypes.find((type) => {
          return (
            type.type ===
            convertSecondsToClosestType(
              props.editingReminder?.offset ?? 60 * 15,
              isAllDay
            )
          )
        }) ?? availableReminderTypes[0])
      : availableReminderTypes[0]
  )

  // Absolute reminder single state (date + optional time)
  const [absoluteDT, setAbsoluteDT] = useState<Dayjs | null>(
    props.editingReminder && props.event?.AllDay === '1'
      ? dayjs(Number(props.editingReminder.AlertTime ?? 0) * 1000)
      : dayjs().startOf('hour')
  )

  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Reset fields when editing an existing reminder or switching all-day context
  useEffect(() => {
    const editing = props.editingReminder

    if (!editing) {
      setMode(ReminderMode.RELATIVE)
      setSearchText(props.event?.AllDay === '1' ? '1' : '15')
      setCurrentType(availableReminderTypes[0])
      setAbsoluteDT(dayjs().startOf('hour'))
      return
    }

    // Absolute reminder: populate date/time and switch mode
    if (editing.isRelativeReminder === '0') {
      setMode(ReminderMode.ABSOLUTE)
      const alertSeconds = Number(editing.AlertTime ?? 0)
      const dt = alertSeconds
        ? dayjs.unix(alertSeconds).tz(getDefaultTimezone())
        : dayjs().startOf('hour')
      setAbsoluteDT(dt)
      return
    }

    // Relative reminder: populate offset text and matching unit
    setMode(ReminderMode.RELATIVE)
    const text = offsetToText(editing) ?? '15'
    setSearchText(text)
    Logger.pink('CustomReminder:Init', text)

    const closestType = convertSecondsToClosestType(
      Number(editing.offset ?? 0),
      isAllDay
    )

    const matchedType =
      availableReminderTypes.find((type) => type.type === closestType) ??
      availableReminderTypes[0]
    setCurrentType(matchedType)
  }, [props.editingReminder, props.event?.AllDay, isAllDay])

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (value.length <= 3 || currentType.type !== ReminderType.MIN) {
      setSearchText(value)
    }
  }

  const showError =
    mode === ReminderMode.RELATIVE &&
    convertToMinutes(searchText, currentType.type) >
      convertToMinutes('4', ReminderType.WEEK)

  function addReminderToEvent() {
    if (showError) return

    let reminder: EventReminder

    if (props.event?.AllDay === '1') {
      let minutes = convertToMinutes(searchText, currentType.type)

      const base = absoluteDT ? absoluteDT.clone() : dayjs()
      const eventStart = dayjs(Number(props.event?.StartDate) * 1000)
      let alertDateTime = eventStart
        .minute(base.minute())
        .hour(base.hour())
        .second(0)
        .subtract(minutes, 'minute')

      if (mode === ReminderMode.RELATIVE) {
        // do nothing, already calculated
      } else {
        // Absolute mode: use selected absolute date/time
        alertDateTime = base.second(0)
      }

      reminder = {
        AlertTime: alertDateTime.unix().toString(),
        isOn: '1',
        isRelativeReminder: '0',
        offset: 0
      }
    } else {
      if (mode === ReminderMode.RELATIVE) {
        Logger.pink('Reminder (relative):', searchText, currentType.type)
        let minutes = convertToMinutes(searchText, currentType.type)
        Logger.pink('Reminder (minutes):', minutes)

        reminder = {
          AlertTime: '',
          isOn: '1',
          isRelativeReminder: '1',
          offset: minutes * 60 // store offset in seconds
        }
      } else {
        // Absolute mode: use selected absolute date/time
        const base = absoluteDT ? absoluteDT.clone() : dayjs().startOf('hour')
        const alertDateTime = base.second(0)

        reminder = {
          AlertTime: alertDateTime.unix().toString(),
          isOn: '1',
          isRelativeReminder: '0',
          offset: 0
        }
      }
    }

    props.setReminders([
      ...(props.reminders.filter((reminder) => {
        if (props.editingReminder?.AlertTime) {
          return reminder.AlertTime !== props.editingReminder.AlertTime
        }

        return reminder.offset !== props.editingReminder?.offset
      }) ?? []),
      reminder
    ])
    Logger.pink('Reminder:', props.reminders)

    if (props.setOpen) props.setOpen(false)
  }

  Logger.blue('CustomReminder:Render', props)

  return (
    <Grid
      container
      alignItems={'start'}
      direction={'column'}
      width={300}
      sx={{ opacity: mode === undefined ? 0 : 1 }}
    >
      <PopupSection title={`${t('customReminder')}`} noTitleLine>
        <Box height={8}></Box>

        {mode === ReminderMode.RELATIVE ? (
          <>
            {/* Input and Select in one row */}
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              mb={2}
              width={'100%'}
            >
              <TextField
                autoFocus
                placeholder="0"
                error={showError}
                type={'number'}
                value={searchText}
                onChange={onSearchChange}
                variant="standard"
                slotProps={{
                  input: {
                    disableUnderline: true
                  },
                  htmlInput: {
                    maxLength:
                      currentType.type === ReminderType.MIN ? 3 : undefined
                  }
                }}
                sx={{
                  flex: 0,
                  minWidth: '40px',
                  maxWidth: '120px',

                  width: '40px',
                  backgroundColor: theme.palette.card.primary,
                  borderRadius: '8px',
                  padding: '0px 8px',
                  display: 'inline-flex',
                  '& .MuiInputBase-input': {
                    padding: 0,
                    textAlign: 'left',
                    fontSize: '14px'
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield'
                  }
                }}
              />
              <FormControl variant="standard">
                <Select
                  value={currentType.type}
                  disableUnderline
                  sx={{
                    fontSize: '14px',
                    height: '28px',
                    width: 'auto',
                    '& .MuiSelect-select': {
                      paddingRight: '24px !important'
                    },
                    '& .MuiSelect-icon': {
                      right: 0
                    }
                  }}
                  onChange={(e) => {
                    const selected = availableReminderTypes.find(
                      (t) => t.type === e.target.value
                    )
                    if (selected) setCurrentType(selected)
                  }}
                >
                  {availableReminderTypes.map((remType) => (
                    <MenuItem
                      key={remType.type}
                      value={remType.type}
                      sx={{ fontSize: '14px' }}
                    >
                      {t('timeBefore', {
                        time: t(unitI18nKey(remType.type), {
                          count: Number(searchText) || 1
                        })
                      }).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {props.event?.AllDay === '1' && (
                <AbsoluteReminderTimePicker
                  absoluteDT={absoluteDT}
                  setAbsoluteDT={setAbsoluteDT}
                  tz={props.event?.TimeZoneNameID}
                />
              )}
            </Box>

            {showError && (
              <Typography color="error" variant="caption" sx={{ mb: 1 }}>
                {t('reminderNotValid')}
              </Typography>
            )}

            <ReminderDivider />
            {/* Toggle to absolute mode */}
            {props.event?.TaskType === TaskType.personalEvent ? (
              <></>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  mt: 1,
                  mb: 2
                }}
                onClick={() => setMode(ReminderMode.ABSOLUTE)}
              >
                {isAllDay
                  ? t(
                      'orChooseReminderTime',
                      'Or choose reminder time before event...'
                    )
                  : t('orChooseReminderTime', 'Or choose reminder time...')}
              </Typography>
            )}
          </>
        ) : (
          <>
            {/* One-line Date + Time display */}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              width={'100%'}
            >
              <Typography variant="body2" sx={{ minWidth: '25px' }}>
                {t('remindOn', 'On')}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <Styled.DatePickPopup
                  open={datePickerOpen}
                  content={
                    <ClickAwayListener
                      mouseEvent="onMouseDown"
                      touchEvent="onTouchStart"
                      onClickAway={() => setDatePickerOpen(false)}
                    >
                      <div>
                        <DateRangePicker
                          mode={'single'}
                          defaultRange={{
                            from: absoluteDT?.toDate(),
                            to: absoluteDT?.toDate()
                          }}
                          defaultDate={absoluteDT?.toDate()}
                          onSelect={(
                            selected: Date | DateRange | undefined
                          ) => {
                            if (selected instanceof Date) {
                              Logger.pink(
                                'CustomReminder: Picked date',
                                selected
                              )
                              const hours =
                                absoluteDT?.hour() ?? (isAllDay ? 9 : 0)
                              const minutes = absoluteDT?.minute() ?? 0

                              const newDate = dayjs(selected)
                                .hour(hours)
                                .minute(minutes)
                                .second(0)

                              setAbsoluteDT(newDate)
                              setDatePickerOpen(false)
                            }
                          }}
                        />
                      </div>
                    </ClickAwayListener>
                  }
                  placement={'left'}
                >
                  <Box
                    onClick={() => setDatePickerOpen(true)}
                    sx={{
                      display: 'inline-flex',
                      width: 'auto',
                      maxWidth: '120px',
                      backgroundColor: theme.palette.card.primary,
                      color: theme.palette.text.primary,
                      borderRadius: '8px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor:
                          theme.palette.button.background.selected
                      }
                    }}
                  >
                    {absoluteDT?.format('ll')}
                  </Box>
                </Styled.DatePickPopup>
                {/* Show time picker for all events in absolute mode */}
                <AbsoluteReminderTimePicker
                  absoluteDT={absoluteDT}
                  setAbsoluteDT={setAbsoluteDT}
                  tz={props.event?.TimeZoneNameID}
                />
              </Box>
            </Box>

            <ReminderDivider />

            {/* Toggle back to relative mode */}
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                mb: 2,
                mt: 1
              }}
              onClick={() => setMode(ReminderMode.RELATIVE)}
            >
              {t('orChooseTimeBefore', 'Or choose time before event...')}
            </Typography>
          </>
        )}

        <Box height={8}></Box>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: 2
          }}
        >
          <Button
            size="xs"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              addReminderToEvent()
            }}
          >
            {t('done')}
          </Button>
        </div>
      </PopupSection>
    </Grid>
  )
}

interface CustomEventReminderPopupProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void

  editingReminder?: EventReminder | undefined
  event?: EventDoc
}

export default function CustomEventReminderPopup(
  props: CustomEventReminderPopupProps
) {
  return (
    <Styled.DatePickPopup
      open={props.open}
      content={
        <CustomReminderContent
          {...props}
          event={props.event}
          editingReminder={props.editingReminder}
          setOpen={props.setOpen}
        />
      }
      placement={'right'}
    >
      {props.children}
    </Styled.DatePickPopup>
  )
}

function convertToMinutes(text: string, type: ReminderType) {
  let minutes = 0
  let typed = Number(text)

  switch (type) {
    case ReminderType.MIN:
      minutes = typed * 1
      break
    case ReminderType.HOUR:
      minutes = typed * 60
      break
    case ReminderType.DAY:
      minutes = typed * 60 * 24
      break
    case ReminderType.WEEK:
      minutes = typed * 60 * 24 * 7
      break
  }

  return minutes
}

function convertSecondsToClosestType(
  seconds: number,
  isAllDay: boolean = false
): ReminderType {
  const SECONDS_IN_HOUR = 3600
  const SECONDS_IN_DAY = 86400
  const SECONDS_IN_WEEK = 604800

  if (!isAllDay && seconds === 0) {
    return ReminderType.HOUR
  }

  // For all-day events, only allow days and weeks
  if (isAllDay) {
    if (seconds % (60 * 60 * 24 * 7) === 0) {
      return ReminderType.WEEK
    } else {
      return ReminderType.DAY
    }
  }

  if (seconds % (60 * 60 * 24 * 7) === 0) {
    return ReminderType.WEEK
  } else if (seconds % (60 * 60 * 24) === 0) {
    return ReminderType.DAY
  } else if (seconds % (60 * 60) === 0) {
    return ReminderType.HOUR
  } else if (seconds % 60 === 0) {
    return ReminderType.MIN
  } else {
    if (seconds < SECONDS_IN_HOUR) {
      return ReminderType.MIN
    } else if (seconds <= SECONDS_IN_DAY) {
      return ReminderType.HOUR
    } else if (seconds <= SECONDS_IN_WEEK) {
      return ReminderType.DAY
    } else {
      return ReminderType.WEEK
    }
  }
}

function offsetToText(editingReminder: EventReminder): string {
  const type = convertSecondsToClosestType(editingReminder.offset)

  let minutes = editingReminder.offset / 60
  let hours = minutes / 60
  let days = hours / 24
  let weeks = days / 7

  if (minutes === 0) {
    return '1'
  }

  if (minutes < 1) minutes = 0
  if (hours < 1) hours = 0
  if (days < 1) days = 0
  if (weeks < 1) weeks = 0

  switch (type) {
    case ReminderType.MIN:
      return minutes.toString()
    case ReminderType.HOUR:
      return hours.toString()
    case ReminderType.DAY:
      return days.toString()
    case ReminderType.WEEK:
      return weeks.toString()
  }
}
